import React from 'react'
import type { Theme, WithStyles, TypographyProps } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type { UpdateableArea, UpdateableAreaDimensions } from '@paintscout/util/builder'
import { getObjectLabels } from '@paintscout/util/builder'
import { Typography, useClientOptions } from '@paintscout/ui'

export const styles = (_theme: Theme) => {
  return createStyles({
    root: {}
  })
}

export interface AreaDimensionsTextProps extends WithStyles<typeof styles>, Omit<TypographyProps, 'classes'> {
  area?: UpdateableArea
}

const RoomDimensions = (props: { dimensions: UpdateableAreaDimensions }) => {
  const { dimensions } = props
  const { options } = useClientOptions()
  const { unit } = getObjectLabels({ options })
  return hasRoomOverrides(dimensions) ? (
    <>
      <span>
        <strong>Linear:</strong>
        {`${dimensions.lnft.useCustom ? dimensions.lnft.custom : dimensions.lnft.default}${unit.abbreviation.linear} `}
      </span>
      <span>
        <strong>Walls:</strong>
        {`${dimensions.sqftWalls.useCustom ? dimensions.sqftWalls.custom : dimensions.sqftWalls.default}${
          unit.abbreviation.square
        } `}
      </span>
      <span>
        <strong>Ceiling:</strong>
        {`${dimensions.sqftCeiling.useCustom ? dimensions.sqftCeiling.custom : dimensions.sqftCeiling.default}${
          unit.abbreviation.square
        } `}
      </span>
      <span>
        <strong>Floor:</strong>
        {`${dimensions.sqftFloor.useCustom ? dimensions.sqftFloor.custom : dimensions.sqftFloor.default}${
          unit.abbreviation.square
        } `}
      </span>
    </>
  ) : (
    <>
      <span>
        <strong>Width:</strong> {`${dimensions.width}${unit.abbreviation.basic} `}
      </span>
      <span>
        <strong>Length:</strong> {`${dimensions.length}${unit.abbreviation.basic} `}
      </span>
      <span>
        <strong>Height:</strong> {`${dimensions.height}${unit.abbreviation.basic} `}
      </span>
    </>
  )
}

const SurfaceDimensions = (props: { dimensions: UpdateableAreaDimensions }) => {
  const { dimensions } = props
  const { options } = useClientOptions()
  const { unit } = getObjectLabels({ options })
  return hasSurfaceOverrides(dimensions) ? (
    <>
      <span>
        <strong>Linear:</strong>{' '}
        {`${dimensions.lnft.useCustom ? dimensions.lnft.custom : dimensions.lnft.default}${unit.abbreviation.linear} `}
      </span>
      <span>
        <strong>{unit.abbreviation.square}:</strong>{' '}
        {`${dimensions.sqft.useCustom ? dimensions.sqft.custom : dimensions.sqft.default}${unit.abbreviation.square} `}
      </span>
    </>
  ) : (
    <>
      <span>
        <strong>Length:</strong> {`${dimensions.length}${unit.abbreviation.basic} `}
      </span>
      <span>
        <strong>Height/Width:</strong> {`${dimensions.height}${unit.abbreviation.linear} `}
      </span>
    </>
  )
}

class AreaDimensionsText extends React.PureComponent<AreaDimensionsTextProps> {
  public render() {
    const { area, ...otherProps } = this.props
    const { dimensions } = area
    return (
      <Typography {...otherProps}>
        {area.areaType === 'room' && <RoomDimensions dimensions={dimensions} />}
        {area.areaType === 'surface' && <SurfaceDimensions dimensions={dimensions} />}
      </Typography>
    )
  }
}

function hasRoomOverrides(dimensions: UpdateableAreaDimensions) {
  if (
    dimensions.lnft.useCustom ||
    dimensions.sqftCeiling.useCustom ||
    dimensions.sqftFloor.useCustom ||
    dimensions.sqftWalls.useCustom
  ) {
    return true
  }
  return false
}

function hasSurfaceOverrides(dimensions: UpdateableAreaDimensions) {
  if (dimensions.lnft.useCustom || dimensions.sqft.useCustom) {
    return true
  }
  return false
}

export default withStyles(styles)(AreaDimensionsText)
